import { Vector3 } from '@technology/web3d';
import { Accessor, batch, createMemo, createSignal, Setter } from 'solid-js';
import { SharedCapabilityProviderBase } from '../base/shared-provider-base';
import {
  CameraStations,
  CameraStationsInput,
  ImageStation,
  PanoramaImageStation,
  PanoramaStation,
  PlanarImageStation,
} from './camera-stations';

interface CameraCacheVersionEntry {
  get: Accessor<number>;
  set(fn: (v: number) => number): void;
}

// Used to group images.
const CAMERA_GROUP_EPSILON_MILLISECONDS = 100;
const MAX_LINK_DISTANCE_MILLISECONDS = 5000;

export interface StationInfo {
  position: Vector3;
  runId: string | null;
  /** `${runId}/${mainImageId}`; unlike the index, stable across run loads and unloads. */
  stationKey: string;
}

/**
 * Each loaded item in this provider is a set of images from camera + camera name.
 * Images from each camera have to be grouped by timestamp.
 * This permits for given position to get all images from all cameras for that position.
 * Due to hardware or other issues in some cases some images might be missing - need to take that into account.
 * If an image is missing it's value will be null.
 */
function stationKeyOf(imageStation: ImageStation): string {
  return `${imageStation.runId}/${imageStation.station.id}`;
}

export class StationsCapabilityProvider extends SharedCapabilityProviderBase<CameraStations> {
  public idKey = 'cameraName' as const;

  // Station information (position + runId) in the same order as stations in the stationList of each model
  public readonly stationsInfo: Accessor<Array<StationInfo>>;

  public readonly stationCount: Accessor<number>;

  public readonly activeRunId: Accessor<string | null>;
  public readonly setActiveRunId: Setter<string | null>;

  /**
   * Non-reactive cache of fully-resolved stations (with tileset URLs) keyed by image id.
   * Prefetch populates this; the loading effect reads it when a station in the provider has no URL.
   * The provider's stationList is never mutated by prefetch — it stays stable after initial load.
   */
  public readonly tilesetCache = new Map<
    string,
    PanoramaImageStation | PlanarImageStation
  >();

  /**
   * Per-camera cache version signals. Each camera's loading effect depends only on its own
   * version, so prefetch for Camera A doesn't cause Camera B's loading effect to re-run.
   */
  private readonly cameraCacheVersions = new Map<
    string,
    CameraCacheVersionEntry
  >();

  /** Cameras that have pending version bumps (coalesced via queueMicrotask). */
  private readonly pendingVersionBumps = new Set<string>();

  /**
   * Returns a reactive accessor for the given camera's cache version.
   * Lazily creates the signal on first access.
   */
  public getCameraCacheVersion(cameraName: string): Accessor<number> {
    return this.getOrCreateCameraVersion(cameraName).get;
  }

  /**
   * Add entries to the tileset cache. The camera's cacheVersion is bumped
   * asynchronously via queueMicrotask so multiple calls in the same tick
   * coalesce into a single version bump (and a single loading-effect re-run).
   */
  public addToCacheAndNotify(
    cameraName: string,
    entries: ReadonlyArray<{
      imageId: string;
      station: PanoramaImageStation | PlanarImageStation;
    }>
  ): void {
    for (const { imageId, station } of entries) {
      this.tilesetCache.set(imageId, station);
    }
    if (entries.length > 0) {
      this.scheduleCacheVersionBump(cameraName);
    }
  }

  /**
   * Schedule a coalesced cache version bump for the given camera.
   * Multiple calls before the microtask runs result in a single bump.
   */
  private scheduleCacheVersionBump(cameraName: string): void {
    if (this.pendingVersionBumps.has(cameraName)) return;
    this.pendingVersionBumps.add(cameraName);
    queueMicrotask(() => {
      this.pendingVersionBumps.delete(cameraName);
      const version = this.getOrCreateCameraVersion(cameraName);
      version.set((v) => v + 1);
    });
  }

  /** Clear cache entries for cameras that no longer exist. */
  public clearCacheForRemovedCameras(): void {
    const currentIds = new Set<string>();
    const currentCameras = new Set<string>();
    for (const camera of this.items()) {
      currentCameras.add(camera.cameraName);
      for (const s of camera.stationList) {
        if (s) currentIds.add(s.station.id);
      }
    }
    for (const key of this.tilesetCache.keys()) {
      if (!currentIds.has(key)) {
        this.tilesetCache.delete(key);
      }
    }
    // Remove version signals for cameras that no longer exist
    for (const cameraName of this.cameraCacheVersions.keys()) {
      if (!currentCameras.has(cameraName)) {
        this.cameraCacheVersions.delete(cameraName);
      }
    }
  }

  private getOrCreateCameraVersion(
    cameraName: string
  ): CameraCacheVersionEntry {
    let entry = this.cameraCacheVersions.get(cameraName);
    if (!entry) {
      const [get, set] = createSignal<number>(0);
      entry = { get, set };
      this.cameraCacheVersions.set(cameraName, entry);
    }
    return entry;
  }

  constructor() {
    super();

    const [activeRunId, setActiveRunId] = createSignal<string | null>(null);
    this.activeRunId = activeRunId;
    this.setActiveRunId = setActiveRunId;

    this.stationsInfo = createMemo(() => {
      const cameraStations = this.items();
      if (!cameraStations.length) {
        return [];
      }

      const stationInfos: StationInfo[] = [];
      const totalStations = cameraStations[0].stationList.length;

      // Use panorama camera position directly when available (stable, no recalc
      // when planar runs are added). Fall back to first available position.
      const panoramaCamera = cameraStations.find(
        (cam) => cam.kind === 'panorama'
      );

      for (let i = 0; i < totalStations; i++) {
        // Try panorama first — its position defines the station
        if (panoramaCamera && i < panoramaCamera.stationList.length) {
          const panoStation = panoramaCamera.stationList[i];
          if (panoStation) {
            stationInfos.push({
              position: new Vector3(
                panoStation.station.position.x,
                panoStation.station.position.y,
                panoStation.station.position.z
              ),
              runId: panoStation.runId,
              stationKey: stationKeyOf(panoStation),
            });
            continue;
          }
        }

        // Fallback: use first camera that has a station at this index
        for (let c = 0; c < cameraStations.length; c++) {
          const list = cameraStations[c].stationList;
          if (i >= list.length) continue;
          const station = list[i];
          if (station) {
            stationInfos.push({
              position: new Vector3(
                station.station.position.x,
                station.station.position.y,
                station.station.position.z
              ),
              runId: station.runId,
              stationKey: stationKeyOf(station),
            });
            break;
          }
        }
      }

      return stationInfos;
    });

    this.stationCount = createMemo(() => {
      const m = this.items();
      if (!m.length) {
        return 0;
      }
      return m[0].stationList.length;
    });
  }

  /**
   * Adds one or more cameras, sorting panoramas before planars internally
   * so that incremental grouping can use panoramas as anchors.
   */
  public addCameraStations(cameras: ReadonlyArray<CameraStationsInput>): void {
    const sorted = cameras.slice().sort((a, b) => {
      if (a.kind === 'panorama' && b.kind !== 'panorama') return -1;
      if (a.kind !== 'panorama' && b.kind === 'panorama') return 1;
      return 0;
    });

    batch(() => {
      for (const cam of sorted) {
        this.addSingleCameraStations(cam.cameraName, cam.kind, cam.stationList);
      }
    });
  }

  private addSingleCameraStations(
    cameraName: string,
    kind: 'panorama' | 'planar',
    stationList: PanoramaImageStation[] | PlanarImageStation[]
  ): void {
    if (stationList.length === 0) {
      return;
    }

    const getCaptureUtc = (
      station: PanoramaImageStation | PlanarImageStation
    ): number => {
      const captureUtc =
        station.kind === 'panorama'
          ? station.station.captureUtc
          : station.captureUtc;
      const time = new Date(captureUtc).getTime();
      if (isNaN(time)) {
        return new Date().getTime();
      }
      return time;
    };

    const areTimestampsClose = (date1: number, date2: number): boolean => {
      return Math.abs(date1 - date2) < CAMERA_GROUP_EPSILON_MILLISECONDS;
    };

    const sorted = stationList
      .slice()
      .sort((a, b) => getCaptureUtc(a) - getCaptureUtc(b));

    const currentItems = this.items();
    const existingCameraIndex = currentItems.findIndex(
      (item) => item.cameraName === cameraName
    );

    let filtered: (PanoramaImageStation | PlanarImageStation)[] = sorted;

    if (existingCameraIndex !== -1) {
      const existingCamera = currentItems[existingCameraIndex];

      if (existingCamera.kind === 'panorama' && kind === 'panorama') {
        const existingIds = new Set(
          existingCamera.stationList
            .filter((s): s is PanoramaImageStation => s !== null)
            .map((s) => s.station.id)
        );
        filtered = (sorted as PanoramaImageStation[]).filter(
          (s) => !existingIds.has(s.station.id)
        );
      } else if (existingCamera.kind === 'planar' && kind === 'planar') {
        const existingKeys = new Set(
          existingCamera.stationList
            .filter((s): s is PlanarImageStation => s !== null)
            .map((s) => `${s.runId}_${s.captureUtc}`)
        );
        filtered = (sorted as PlanarImageStation[]).filter(
          (s) => !existingKeys.has(`${s.runId}_${s.captureUtc}`)
        );
      } else if (existingCamera.kind !== kind) {
        return;
      }

      if (filtered.length === 0) return;
    }

    const camera = { cameraName, kind, stationList: filtered };

    const hasPanoramaInExisting = currentItems.some(
      (item) =>
        item.kind === 'panorama' && item.stationList.some((s) => s !== null)
    );

    if (currentItems.length > 0 && hasPanoramaInExisting) {
      this.extendExistingGroups(currentItems, [camera], getCaptureUtc);
    } else {
      // Merge existing cameras (non-null stations) with the new camera for full regroup
      const allCameras: Array<{
        cameraName: string;
        kind: 'panorama' | 'planar';
        stationList: (PanoramaImageStation | PlanarImageStation)[];
      }> = currentItems.map((item) => ({
        cameraName: item.cameraName,
        kind: item.kind,
        stationList: item.stationList.filter(
          (s): s is PanoramaImageStation | PlanarImageStation => s !== null
        ),
      }));

      const existingIdx = allCameras.findIndex(
        (c) => c.cameraName === cameraName
      );
      if (existingIdx !== -1) {
        allCameras[existingIdx] = {
          ...allCameras[existingIdx],
          stationList: [...allCameras[existingIdx].stationList, ...filtered],
        };
      } else {
        allCameras.push(camera);
      }

      this.buildGroupsFromScratch(
        allCameras,
        getCaptureUtc,
        areTimestampsClose
      );
    }
  }

  /**
   * Extends existing grouped stationLists with new stations from a new run.
   * Panorama stations become new groups (appended). Planar stations are
   * assigned to the closest existing or new panorama group via binary search.
   * O(M log N) where M = new stations, N = existing groups.
   */
  private extendExistingGroups(
    currentItems: CameraStations[],
    camerasToProcess: Array<{
      cameraName: string;
      kind: 'panorama' | 'planar';
      stationList: (PanoramaImageStation | PlanarImageStation)[];
    }>,
    getCaptureUtc: (
      station: PanoramaImageStation | PlanarImageStation
    ) => number
  ): void {
    // Clone existing stationLists (shallow copy of each array)
    const output: CameraStations[] = currentItems.map((item) => ({
      ...item,
      stationList: item.stationList.slice(),
    })) as CameraStations[];

    // Add entries for cameras that don't exist yet
    for (const cam of camerasToProcess) {
      if (!output.some((item) => item.cameraName === cam.cameraName)) {
        const groupCount = output[0]?.stationList.length ?? 0;
        output.push({
          cameraName: cam.cameraName,
          kind: cam.kind,
          stationList: new Array(groupCount).fill(null),
        } as CameraStations);
      }
    }

    // Build sorted timestamps from the panorama camera for binary search
    const panoramaCam = output.find((item) => item.kind === 'panorama');
    const groupTimestamps: number[] = [];
    if (panoramaCam) {
      for (const s of panoramaCam.stationList) {
        groupTimestamps.push(
          s !== null ? getCaptureUtc(s) : Number.MAX_SAFE_INTEGER
        );
      }
    }

    // Append new panorama stations as new groups.
    // New panorama stations are already sorted by captureUtc (from dedup phase).
    // Verify they come after existing groups so binary search stays valid.
    const newPanoramas = camerasToProcess.filter(
      (cam) => cam.kind === 'panorama'
    );

    if (newPanoramas.length > 0) {
      const lastExistingTs =
        groupTimestamps.length > 0
          ? groupTimestamps[groupTimestamps.length - 1]
          : -Infinity;
      const firstNewTs =
        newPanoramas[0].stationList.length > 0
          ? getCaptureUtc(newPanoramas[0].stationList[0])
          : Infinity;

      if (firstNewTs < lastExistingTs) {
        // Runs overlap in time — fall back to full re-group with ALL cameras
        const allCameras = new Map<
          string,
          {
            cameraName: string;
            kind: 'panorama' | 'planar';
            stationList: (PanoramaImageStation | PlanarImageStation)[];
          }
        >();
        for (const item of currentItems) {
          allCameras.set(item.cameraName, {
            cameraName: item.cameraName,
            kind: item.kind,
            stationList: item.stationList.filter(
              (s): s is PanoramaImageStation | PlanarImageStation => s !== null
            ),
          });
        }
        for (const cam of camerasToProcess) {
          const existing = allCameras.get(cam.cameraName);
          if (existing) {
            existing.stationList = [
              ...existing.stationList,
              ...cam.stationList,
            ];
          } else {
            allCameras.set(cam.cameraName, {
              ...cam,
              stationList: cam.stationList.slice(),
            });
          }
        }
        this.buildGroupsFromScratch(
          [...allCameras.values()],
          getCaptureUtc,
          (d1: number, d2: number) =>
            Math.abs(d1 - d2) < CAMERA_GROUP_EPSILON_MILLISECONDS
        );
        return;
      }
    }

    for (const panoCam of newPanoramas) {
      for (const station of panoCam.stationList) {
        const ts = getCaptureUtc(station);
        for (const cam of output) {
          if (cam.cameraName === panoCam.cameraName) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- stationList union type prevents direct push of a concrete type
            (cam.stationList as any[]).push(station);
          } else {
            cam.stationList.push(null);
          }
        }
        groupTimestamps.push(ts);
      }
    }

    // Per-run lookup: a planar only links to a slot whose panoramas include
    // its runId. Check all pano cameras since the first may be null at a slot.
    const slotIndicesByRun = new Map<string, number[]>();
    const slotTimestampsByRun = new Map<string, number[]>();
    const totalSlots = output[0]?.stationList.length ?? 0;
    for (let i = 0; i < totalSlots; i++) {
      const runIdsAtSlot = new Set<string>();
      let slotTimestamp: number | undefined;
      for (const cam of output) {
        if (cam.kind !== 'panorama') continue;
        const s = cam.stationList[i];
        if (s === null) continue;
        runIdsAtSlot.add(s.runId);
        if (slotTimestamp === undefined) {
          slotTimestamp = getCaptureUtc(s);
        }
      }
      if (slotTimestamp === undefined) continue;
      for (const runId of runIdsAtSlot) {
        let indices = slotIndicesByRun.get(runId);
        if (!indices) {
          indices = [];
          slotIndicesByRun.set(runId, indices);
        }
        indices.push(i);
        let timestamps = slotTimestampsByRun.get(runId);
        if (!timestamps) {
          timestamps = [];
          slotTimestampsByRun.set(runId, timestamps);
        }
        timestamps.push(slotTimestamp);
      }
    }

    const maxLinkDistanceByRun = new Map<string, number>();
    for (const [runId, timestamps] of slotTimestampsByRun) {
      maxLinkDistanceByRun.set(
        runId,
        this.calculateMaxLinkDistance(timestamps.map((t) => ({ timestamp: t })))
      );
    }

    const newPlanars = camerasToProcess.filter((cam) => cam.kind === 'planar');
    for (const planarCam of newPlanars) {
      const outputCam = output.find(
        (item) => item.cameraName === planarCam.cameraName
      );
      if (!outputCam) continue;

      // Track distance for each assigned slot so closer planars can replace farther ones
      const slotDistances = new Map<number, number>();
      for (const station of planarCam.stationList) {
        const planarRunId = station.runId;
        const candidateSlots = slotIndicesByRun.get(planarRunId);
        const candidateTimestamps = slotTimestampsByRun.get(planarRunId);
        if (
          !candidateSlots ||
          !candidateTimestamps ||
          candidateSlots.length === 0
        ) {
          continue;
        }

        const planarTime = getCaptureUtc(station);
        const localIdx = findClosestIndex(candidateTimestamps, planarTime);
        if (localIdx === -1) continue;

        const closestIdx = candidateSlots[localIdx];
        const distance = Math.abs(candidateTimestamps[localIdx] - planarTime);
        const maxLinkDistance = maxLinkDistanceByRun.get(planarRunId) ?? 0;
        if (distance > maxLinkDistance) continue;

        const existingDistance = slotDistances.get(closestIdx);
        if (
          outputCam.stationList[closestIdx] === null ||
          (existingDistance !== undefined && distance < existingDistance)
        ) {
          outputCam.stationList[closestIdx] = station as PlanarImageStation;
          slotDistances.set(closestIdx, distance);
        }
      }
    }

    this.setItems(output);
  }

  /**
   * Full grouping from scratch. Used on the first run when no groups exist.
   */
  private buildGroupsFromScratch(
    camerasToProcess: Array<{
      cameraName: string;
      kind: 'panorama' | 'planar';
      stationList: (PanoramaImageStation | PlanarImageStation)[];
    }>,
    getCaptureUtc: (
      station: PanoramaImageStation | PlanarImageStation
    ) => number,
    areTimestampsClose: (date1: number, date2: number) => boolean
  ): void {
    const itemsToAdd: CameraStations[] = [];
    for (const cam of camerasToProcess) {
      if (!itemsToAdd.some((item) => item.cameraName === cam.cameraName)) {
        itemsToAdd.push({
          cameraName: cam.cameraName,
          kind: cam.kind,
          stationList: [],
        } as CameraStations);
      }
    }

    const existingItems: CameraStations[] = camerasToProcess.map((cam) => ({
      cameraName: cam.cameraName,
      kind: cam.kind,
      stationList: cam.stationList.slice(),
    })) as CameraStations[];

    const panoramaCameras = existingItems.filter(
      (item): item is CameraStations & { kind: 'panorama' } =>
        item.kind === 'panorama'
    );
    const planarCameras = existingItems.filter(
      (item): item is CameraStations & { kind: 'planar' } =>
        item.kind === 'planar'
    );

    const hasPanoramaStations = panoramaCameras.some(
      (cam) => cam.stationList.filter((s) => s !== null).length > 0
    );

    if (hasPanoramaStations) {
      this.groupStationsWithPanoramaAnchor(
        panoramaCameras,
        planarCameras,
        itemsToAdd,
        getCaptureUtc
      );
    } else {
      this.groupStationsByTimeEpsilon(
        existingItems,
        itemsToAdd,
        getCaptureUtc,
        areTimestampsClose
      );
    }

    this.setItems(itemsToAdd);
  }

  /**
   * Groups stations using panorama stations as anchors.
   * Panoramas from different cameras at similar times are grouped together.
   * Each panorama group defines a station, and the closest planar from each
   * planar camera is linked to it. Each planar is only used once.
   */
  private groupStationsWithPanoramaAnchor(
    panoramaCameras: Array<CameraStations & { kind: 'panorama' }>,
    planarCameras: Array<CameraStations & { kind: 'planar' }>,
    itemsToAdd: CameraStations[],
    getCaptureUtc: (
      station: PanoramaImageStation | PlanarImageStation
    ) => number
  ): void {
    // Collect all panorama stations with their timestamps and camera info
    const allPanoramas: Array<{
      cameraName: string;
      station: PanoramaImageStation;
      timestamp: number;
    }> = [];

    for (const panoCamera of panoramaCameras) {
      for (const station of panoCamera.stationList) {
        if (station !== null) {
          allPanoramas.push({
            cameraName: panoCamera.cameraName,
            station,
            timestamp: getCaptureUtc(station),
          });
        }
      }
    }

    // Sort panoramas by timestamp
    allPanoramas.sort((a, b) => a.timestamp - b.timestamp);

    // Group panoramas that are within epsilon of each other
    // This handles multiple panorama cameras capturing at the same time
    type PanoramaGroup = {
      timestamp: number; // Reference timestamp for the group
      panoramas: Array<{
        cameraName: string;
        station: PanoramaImageStation;
      }>;
    };

    const panoramaGroups: PanoramaGroup[] = [];

    for (const pano of allPanoramas) {
      // Check if this panorama belongs to an existing group
      const existingGroup = panoramaGroups.find(
        (group) =>
          Math.abs(group.timestamp - pano.timestamp) <
            CAMERA_GROUP_EPSILON_MILLISECONDS &&
          !group.panoramas.some((p) => p.cameraName === pano.cameraName)
      );

      if (existingGroup) {
        existingGroup.panoramas.push({
          cameraName: pano.cameraName,
          station: pano.station,
        });
      } else {
        // Create a new group
        panoramaGroups.push({
          timestamp: pano.timestamp,
          panoramas: [{ cameraName: pano.cameraName, station: pano.station }],
        });
      }
    }

    // For each planar camera, assign each planar to its closest panorama group
    // Structure: Map<planarCameraName, Map<groupIndex, PlanarImageStation>>
    const planarAssignments = new Map<
      string,
      Map<number, PlanarImageStation>
    >();

    // Per-run lookup: a planar only links to a group whose panoramas include
    // its runId. Cross-run mixing could survive removal as orphan slots.
    const groupIndicesByRun = new Map<string, number[]>();
    const groupTimestampsByRun = new Map<string, number[]>();
    for (let i = 0; i < panoramaGroups.length; i++) {
      const runIdsInGroup = new Set<string>();
      for (const p of panoramaGroups[i].panoramas) {
        runIdsInGroup.add(p.station.runId);
      }
      for (const runId of runIdsInGroup) {
        let indices = groupIndicesByRun.get(runId);
        if (!indices) {
          indices = [];
          groupIndicesByRun.set(runId, indices);
        }
        indices.push(i);
        let timestamps = groupTimestampsByRun.get(runId);
        if (!timestamps) {
          timestamps = [];
          groupTimestampsByRun.set(runId, timestamps);
        }
        timestamps.push(panoramaGroups[i].timestamp);
      }
    }

    // Per-run threshold so each run's cadence isn't halved by interleaving.
    const maxLinkDistanceByRun = new Map<string, number>();
    for (const [runId, timestamps] of groupTimestampsByRun) {
      maxLinkDistanceByRun.set(
        runId,
        this.calculateMaxLinkDistance(timestamps.map((t) => ({ timestamp: t })))
      );
    }

    for (const planarCamera of planarCameras) {
      const assignments = new Map<number, PlanarImageStation>();
      planarAssignments.set(planarCamera.cameraName, assignments);

      for (const planarStation of planarCamera.stationList) {
        if (planarStation === null) {
          continue;
        }

        const planarRunId = planarStation.runId;
        const candidateIndices = groupIndicesByRun.get(planarRunId);
        const candidateTimestamps = groupTimestampsByRun.get(planarRunId);
        if (
          !candidateIndices ||
          !candidateTimestamps ||
          candidateIndices.length === 0
        ) {
          continue;
        }

        const planarTime = getCaptureUtc(planarStation);

        // Binary search restricted to the planar's own run
        const localIdx = findClosestIndex(candidateTimestamps, planarTime);
        if (localIdx === -1) {
          continue;
        }

        const closestGroupIndex = candidateIndices[localIdx];
        const closestDistance = Math.abs(
          candidateTimestamps[localIdx] - planarTime
        );
        const maxLinkDistance = maxLinkDistanceByRun.get(planarRunId) ?? 0;

        if (closestDistance <= maxLinkDistance) {
          const existingAssignment = assignments.get(closestGroupIndex);
          if (existingAssignment) {
            const existingDistance = Math.abs(
              panoramaGroups[closestGroupIndex].timestamp -
                getCaptureUtc(existingAssignment)
            );
            if (closestDistance < existingDistance) {
              assignments.set(closestGroupIndex, planarStation);
            }
          } else {
            assignments.set(closestGroupIndex, planarStation);
          }
        }
      }
    }

    // Build the station groups based on panorama groups
    for (let groupIndex = 0; groupIndex < panoramaGroups.length; groupIndex++) {
      const group = panoramaGroups[groupIndex];

      for (const itemToAdd of itemsToAdd) {
        if (itemToAdd.kind === 'panorama') {
          // Check if this panorama camera has a station in this group
          const panoForCamera = group.panoramas.find(
            (p) => p.cameraName === itemToAdd.cameraName
          );
          if (panoForCamera) {
            (
              itemToAdd as CameraStations & { kind: 'panorama' }
            ).stationList.push(panoForCamera.station);
          } else {
            // No panorama for this camera at this time
            (
              itemToAdd as CameraStations & { kind: 'panorama' }
            ).stationList.push(null);
          }
        } else {
          // Planar camera - check if we have an assignment for this group
          const cameraAssignments = planarAssignments.get(itemToAdd.cameraName);
          const assignedPlanar = cameraAssignments?.get(groupIndex) ?? null;
          (itemToAdd as CameraStations & { kind: 'planar' }).stationList.push(
            assignedPlanar
          );
        }
      }
    }
  }

  /**
   * Calculates the maximum link distance for planar-to-panorama assignments.
   * Uses half the average interval between panorama groups, ensuring planars
   * that are too far from any panorama aren't incorrectly linked.
   *
   * @param panoramaGroups The grouped panorama stations sorted by timestamp
   * @returns Maximum distance in milliseconds for linking a planar to a panorama
   */
  private calculateMaxLinkDistance(
    panoramaGroups: Array<{ timestamp: number }>
  ): number {
    // If only one panorama group, use a generous default (no interval to calculate)
    if (panoramaGroups.length <= 1) {
      // Default to 5 seconds if we can't calculate an interval
      return MAX_LINK_DISTANCE_MILLISECONDS;
    }

    // Calculate intervals between consecutive panorama groups
    const intervals: number[] = [];
    for (let i = 1; i < panoramaGroups.length; i++) {
      const interval =
        panoramaGroups[i].timestamp - panoramaGroups[i - 1].timestamp;
      intervals.push(interval);
    }

    // Use the average interval
    const avgInterval =
      intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;

    // Max link distance is half the average interval
    // This ensures a planar is never more than halfway between two panoramas
    // eslint-disable-next-line no-magic-numbers
    const maxLinkDistance = avgInterval / 2;

    // Apply a minimum threshold to handle edge cases with very close panoramas
    const minimumThreshold = CAMERA_GROUP_EPSILON_MILLISECONDS;

    return Math.max(maxLinkDistance, minimumThreshold);
  }

  /**
   * Fallback grouping algorithm when no panoramas are present.
   * Groups stations by time epsilon (original algorithm).
   */
  private groupStationsByTimeEpsilon(
    existingItems: CameraStations[],
    itemsToAdd: CameraStations[],
    getCaptureUtc: (
      station: PanoramaImageStation | PlanarImageStation
    ) => number,
    areTimestampsClose: (date1: number, date2: number) => boolean
  ): void {
    // Track the current position in each camera's station list
    const cameraIndices: Record<string, number> = {};

    // Initialize indices for each camera
    for (const item of existingItems) {
      cameraIndices[item.cameraName] = 0;
    }

    // Iterate through all stations lists and find a station with the lowest timestamp.
    // Then find another station with a timestamp within epsilon and add that
    // station to the group (in the camera list with the same index).

    while (true) {
      // Find the station with the earliest timestamp among all remaining stations
      let earliestTime: number | null = null;
      let earliestCameraStation: CameraStations | null = null;

      // Look through all cameras to find the earliest remaining station
      for (const item of existingItems) {
        const currentIndex = cameraIndices[item.cameraName];
        if (currentIndex < item.stationList.length) {
          const imageStation = item.stationList[currentIndex];
          if (imageStation) {
            const timestamp = getCaptureUtc(imageStation);
            if (earliestTime === null || timestamp < earliestTime) {
              earliestTime = timestamp;
              earliestCameraStation = item;
            }
          }
        }
      }

      // If no stations found, we're done
      if (earliestTime === null || earliestCameraStation === null) {
        break;
      }

      // Find all stations from all cameras that are within epsilon of the earliest time
      const stationsForThisGroup: Array<{
        camera: string;
        station: PanoramaImageStation | PlanarImageStation | null;
      }> = [];

      for (const item of existingItems) {
        const currentIndex = cameraIndices[item.cameraName];
        if (currentIndex >= item.stationList.length) {
          // No more stations for this camera.
          continue;
        }
        // The earliest camera is added automatically.
        if (item.cameraName === earliestCameraStation.cameraName) {
          stationsForThisGroup.push({
            camera: earliestCameraStation.cameraName,
            station: earliestCameraStation.stationList[currentIndex],
          });
          // Advance the index for this camera since we're processing this station
          cameraIndices[item.cameraName]++;
          continue;
        }
        // Find another camera that is close to the earliest camera.
        const imageStation = item.stationList[currentIndex];
        if (imageStation) {
          const timestamp = getCaptureUtc(imageStation);
          if (areTimestampsClose(timestamp, earliestTime)) {
            stationsForThisGroup.push({
              camera: item.cameraName,
              station: imageStation,
            });
            // Advance the index for this camera since we're processing this station
            cameraIndices[item.cameraName]++;
          }
        }
      }

      // Add stations to the appropriate camera groups for this time index
      for (const itemToAdd of itemsToAdd) {
        const stationForThisCamera = stationsForThisGroup.find(
          (s) => s.camera === itemToAdd.cameraName
        );

        if (stationForThisCamera) {
          // Add the station with the current index
          if (itemToAdd.kind === 'panorama') {
            (
              itemToAdd as CameraStations & { kind: 'panorama' }
            ).stationList.push(
              stationForThisCamera.station as PanoramaImageStation
            );
          } else {
            (itemToAdd as CameraStations & { kind: 'planar' }).stationList.push(
              stationForThisCamera.station as PlanarImageStation
            );
          }
        } else {
          // No station for this camera at this time, add null.
          if (itemToAdd.kind === 'panorama') {
            (
              itemToAdd as CameraStations & { kind: 'panorama' }
            ).stationList.push(null);
          } else {
            (itemToAdd as CameraStations & { kind: 'planar' }).stationList.push(
              null
            );
          }
        }
      }
    }
  }

  public removeStations(cameraName: string): void {
    const model = this.getById(cameraName);
    if (model) {
      this.removeItem(model);
    }
  }

  /**
   * Replaces panorama-station data in place for every entry whose `runId`
   * matches `runId`. The position layout (length, indices, grouping with
   * other cameras) is preserved — only the `station` payload swaps. Cache
   * versions for affected cameras are bumped so image capabilities re-run
   * their loading effects.
   *
   * Intended for E57 / SDS panorama rotation, where new quaternions need to
   * propagate to viewers without unloading and re-adding the run (which
   * would drop `activeRunId` and break cross-pane sync).
   *
   * Callers are responsible for unloading the existing panorama models from
   * each viewer beforehand so that the reload picks up the updated metadata
   * (panorama models cache their quaternion at load time).
   */
  public updatePanoramaStationsForRun(
    runId: string,
    update: (station: PanoramaStation) => PanoramaStation
  ): void {
    const items = this.items();
    const affectedCameras = new Set<string>();

    const newItems = items.map((cam) => {
      if (cam.kind !== 'panorama') return cam;
      let listModified = false;
      const newList = cam.stationList.map((entry) => {
        if (!entry || entry.runId !== runId) return entry;
        const updatedStation = update(entry.station);
        if (updatedStation === entry.station) return entry;
        listModified = true;
        return { ...entry, station: updatedStation };
      });
      if (!listModified) return cam;
      affectedCameras.add(cam.cameraName);
      return { ...cam, stationList: newList };
    }) as CameraStations[];

    if (affectedCameras.size === 0) return;

    this.setItems(newItems);
    for (const cameraName of affectedCameras) {
      this.scheduleCacheVersionBump(cameraName);
    }
  }

  public removeAllStationsForRuns(runIds: string[]): void {
    const items = this.items();
    if (items.length === 0) return;

    const runIdSet = new Set(runIds);
    const panoramaCamera = items.find((cam) => cam.kind === 'panorama');
    const totalStations = (panoramaCamera ?? items[0]).stationList.length;

    // First pass: null-out stations that belong to the removed runs while
    // preserving the group slot (keeps arrays aligned across cameras).
    const nulled = items.map((item) => ({
      ...item,
      stationList: item.stationList.map((sl) => {
        if (sl === null) return null;
        return runIdSet.has(sl.runId) ? null : sl;
      }),
    })) as CameraStations[];

    // Second pass: drop group indices where ALL cameras are null (groups that
    // only existed because of the removed run).
    const indicesToKeep: number[] = [];
    for (let i = 0; i < totalStations; i++) {
      if (nulled.some((cam) => cam.stationList[i] !== null)) {
        indicesToKeep.push(i);
      }
    }

    const result = nulled.map((cam) => ({
      ...cam,
      stationList: indicesToKeep.map((i) => cam.stationList[i]),
    })) as CameraStations[];

    if (result.every((item) => item.stationList.length === 0)) {
      this.removeAllStations();
    } else {
      this.setItems(result);
    }
  }

  public removeAllStations(): void {
    this.setItems([]);
    this.tilesetCache.clear();
    this.cameraCacheVersions.clear();
    this.setActiveRunId(null);
  }

  public cleanup(): Promise<void> {
    this.removeAllStations();
    return Promise.resolve(undefined);
  }
}

/**
 * Binary search on a sorted array for the index whose value is closest to target.
 * Returns -1 if the array is empty.
 */
function findClosestIndex(sortedValues: number[], target: number): number {
  if (sortedValues.length === 0) return -1;

  let low = 0;
  let high = sortedValues.length - 1;

  while (low < high) {
    const mid = (low + high) >>> 1;
    if (sortedValues[mid] < target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  // low is the insertion point; compare with neighbors
  if (low === 0) return 0;
  if (low >= sortedValues.length) return sortedValues.length - 1;

  const distPrev = Math.abs(sortedValues[low - 1] - target);
  const distCurr = Math.abs(sortedValues[low] - target);
  return distPrev <= distCurr ? low - 1 : low;
}

/**
 * Calculates the average position from a collection of station positions
 * @param stations Array of stations with position data
 * @returns Vector3 with the average position, or null if no valid stations found
 */
export function calculateAverageStationPosition(
  stations: Array<PanoramaImageStation | PlanarImageStation | null>
): Vector3 | null {
  const validStations = stations.filter((st) => st !== null).map((st) => st);

  if (validStations.length === 0) {
    return null;
  }

  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;
  let count = 0;

  for (const station of validStations) {
    if (
      station.station.position.x !== undefined &&
      station.station.position.y !== undefined &&
      station.station.position.z !== undefined
    ) {
      sumX += station.station.position.x;
      sumY += station.station.position.y;
      sumZ += station.station.position.z;
      count++;
    }
  }

  if (count === 0) {
    return null;
  }

  return new Vector3(sumX / count, sumY / count, sumZ / count);
}
